import React from 'react';
import {
    Box, Typography, TextField, Checkbox, FormControlLabel, Radio, Autocomplete, Button,
} from '@mui/material';
import { PhoneInput } from 'react-international-phone';
import 'react-international-phone/build/index.css';

import { findCountry, getPhoneCountries, getStatesByCountry, useGeo } from '../../../../utils/geo';
import { getPhoneDialCode, splitPhoneByDialCode } from '../../../../utils/utils';

/**
 * Form pieces for the sign-up chatbot, one look for every step. Labels carry the
 * document's asterisks for required fields.
 */

// Themeable via CSS custom properties (distinct from the app-wide `--brand-*`
// white-label vars) so a host page can retint these shared form pieces —
// e.g. the Powered by DQ chatbot page sets these to its own rust-orange —
// without affecting apply-v2, which never sets them and keeps this exact hex.
export const ORANGE = 'var(--pbdq-accent, #E8580C)';
export const ORANGE_TINT = 'var(--pbdq-accent-tint, #FFF4ED)';
export const ORANGE_HOVER = 'var(--pbdq-accent-hover, #C2410C)';
export const MUTED = 'var(--brand-text-muted, #6B7280)';
export const BORDER = 'var(--brand-border, #E5E7EB)';

export const FieldLabel = ({ children, help }) => (
    <Box sx={{ mb: 0.75 }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'var(--brand-text, #111827)' }}>{children}</Typography>
        {help && <Typography sx={{ fontSize: '0.8rem', color: MUTED, mt: 0.25, lineHeight: 1.5 }}>{help}</Typography>}
    </Box>
);

/**
 * An agent message from signup.text, laid out as the document lays it out: one line
 * per entry, the document's bold line bold. `value` is a string, a { bold } line, or
 * a list of them.
 */
export const Message = ({ value }) => [].concat(value).map((line, i) => {
    // Not `line?.bold`: every string has a (legacy) String.prototype.bold method.
    const bold = line !== null && typeof line === 'object' && 'bold' in line;
    return (
        <Box key={i} component="span" sx={{ display: 'block', mt: i ? 0.75 : 0, fontWeight: bold ? 700 : 'inherit' }}>
            {bold ? line.bold : line}
        </Box>
    );
});

export const Section = ({ title, children }) => (
    <Box sx={{ mt: 2.5 }}>
        {title && <Typography sx={{ fontWeight: 800, fontSize: '0.95rem', mb: 1.25, color: 'var(--brand-text, #111827)' }}>{title}</Typography>}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.75 }}>{children}</Box>
    </Box>
);

export const TextInput = ({ label, help, value, onChange, type = 'text', multiline = false, placeholder, error, inputProps, endAdornment }) => (
    <Box>
        {label && <FieldLabel help={help}>{label}</FieldLabel>}
        <TextField
            fullWidth size="small" type={type} multiline={multiline} minRows={multiline ? 3 : undefined}
            value={value ?? ''} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
            error={!!error} helperText={error || undefined}
            inputProps={inputProps}
            InputProps={endAdornment ? { endAdornment } : undefined}
        />
    </Box>
);

export const NumberInput = ({ label, help, value, onChange, min = 0, error, suffix }) => (
    <TextInput
        label={label} help={help} value={value} error={error}
        onChange={(v) => onChange(v.replace(/[^\d]/g, '').slice(0, 9))}
        inputProps={{ inputMode: 'numeric', min }}
        endAdornment={suffix ? <Typography sx={{ fontSize: '0.85rem', color: MUTED, ml: 1 }}>{suffix}</Typography> : undefined}
    />
);

/** A single choice as a list of cards (the document's "(Single-select)"). */
export const SelectOne = ({ label, help, options, value, onChange }) => (
    <Box>
        {label && <FieldLabel help={help}>{label}</FieldLabel>}
        <Box role="radiogroup" sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {options.map((o) => {
                const selected = value === o.value;
                return (
                    <Box
                        key={o.value} role="radio" aria-checked={selected} tabIndex={0}
                        onClick={() => onChange(o.value)}
                        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onChange(o.value); } }}
                        sx={{
                            display: 'flex', gap: 1, alignItems: 'flex-start', px: 1.5, py: 1.1, borderRadius: '10px', cursor: 'pointer',
                            border: `1.5px solid ${selected ? ORANGE : BORDER}`, background: selected ? ORANGE_TINT : 'var(--brand-surface, #fff)',
                            '&:focus-visible': { outline: `2px solid ${ORANGE}`, outlineOffset: 2 },
                        }}
                    >
                        <Radio checked={selected} size="small" sx={{ p: 0.25, color: MUTED, '&.Mui-checked': { color: ORANGE } }} tabIndex={-1}/>
                        <Box>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.9rem' }}>{o.label}</Typography>
                            {(o.description || o.detail) && <Typography sx={{ fontSize: '0.8rem', color: MUTED, lineHeight: 1.5 }}>{o.description || o.detail}</Typography>}
                        </Box>
                    </Box>
                );
            })}
        </Box>
    </Box>
);

/** A multi-select as checkboxes ("Select all that apply"). "None" clears the others. */
export const SelectMany = ({ label, help = 'Select all that apply.', options, value = [], onChange, noneLabel }) => {
    const toggle = (v) => onChange(value.includes(v) ? value.filter((x) => x !== v) : [...value, v]);
    return (
        <Box>
            {label && <FieldLabel help={help}>{label}</FieldLabel>}
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {options.map((o) => (
                    <FormControlLabel
                        key={o.value}
                        control={<Checkbox size="small" checked={value.includes(o.value)} onChange={() => toggle(o.value)} sx={{ '&.Mui-checked': { color: ORANGE } }}/>}
                        label={(
                            <Box>
                                <Typography sx={{ fontSize: '0.9rem' }}>{o.label}</Typography>
                                {o.detail && <Typography sx={{ fontSize: '0.78rem', color: MUTED, lineHeight: 1.45 }}>{o.detail}</Typography>}
                            </Box>
                        )}
                        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.4 } }}
                    />
                ))}
                {noneLabel && (
                    <FormControlLabel
                        control={<Checkbox size="small" checked={value.length === 0} onChange={() => onChange([])} sx={{ '&.Mui-checked': { color: ORANGE } }}/>}
                        label={<Typography sx={{ fontSize: '0.9rem' }}>{noneLabel}</Typography>}
                    />
                )}
            </Box>
        </Box>
    );
};

/*
 * Saves the country's ISO code (Oct 2026 — it saved the name before). `value` may
 * still be a name from an application started earlier; it is shown either way.
 */
export const CountrySelect = ({ label, value, onChange }) => {
    const { countries } = useGeo();
    return (
    <Box>
        {label && <FieldLabel>{label}</FieldLabel>}
        <Autocomplete
            size="small"
            options={countries}
            getOptionLabel={(o) => o.label || ''}
            isOptionEqualToValue={(o, v) => o.code === v?.code}
            value={findCountry(value) || null}
            onChange={(_, v) => onChange(v?.code || '')}
            renderInput={(params) => <TextField {...params} placeholder="Select a country"/>}
            componentsProps={{ popper: { sx: { zIndex: 1400 } } }}
        />
    </Box>
    );
};

/*
 * `value` ("+65 61234567") seeds the input once, when it mounts — so a form shown
 * again (after Go back) shows the number already entered. Only seeded, never fed
 * back: feeding it back fights the input's cursor tracking (as in Settings).
 */
export const PhoneField = ({ label, value, onChange }) => {
    useGeo();
    const [seed] = React.useState(value || '');
    return (
    <Box>
        {label && <FieldLabel>{label}</FieldLabel>}
        <PhoneInput
            defaultCountry="sg"
            countries={getPhoneCountries()}
            value={seed}
            disableDialCodePrefill
            onChange={(phone, iso2) => {
                if (!phone) { onChange({ phone_country_code: '', phone_number: '' }); return; }
                const dial = getPhoneDialCode(iso2);
                onChange({ phone_country_code: dial, phone_number: splitPhoneByDialCode(phone, dial) });
            }}
            inputProps={{ autoComplete: 'tel' }}
            inputStyle={{ flex: 1, minWidth: 0 }}
            style={{ width: '100%', '--react-international-phone-height': '40px', '--react-international-phone-border-color': 'rgba(0,0,0,0.23)' }}
        />
    </Box>
    );
};

/*
 * `country` (the country's code, as CountrySelect gives it — or a name from an
 * older application) turns State into a pick-list of that country's states —
 * Settings uses the same list. Stored as the state's ISO 3166-2 code (Oct 2026 —
 * the English name before; an old name still shows). Free text when no country
 * is passed, or the country has no list. Callers that pass it clear the state
 * when the country changes.
 */
const StateSelect = ({ country, value, onChange }) => {
    useGeo();
    const code = findCountry(country)?.code || '';
    const states = getStatesByCountry(code);
    if (country && states.length === 0) {
        return <TextInput value={value} onChange={onChange} placeholder="State, province or region"/>;
    }
    return (
        <Autocomplete
            size="small"
            options={states}
            getOptionLabel={(o) => o.name || ''}
            isOptionEqualToValue={(o, v) => o.code === v?.code}
            value={states.find((st) => st.name === value || st.code === value) || null}
            onChange={(_, v) => onChange(v?.code || '')}
            disabled={!country}
            renderInput={(params) => (
                <TextField {...params} placeholder={country ? 'State, province or region' : 'Select a country first'}/>
            )}
            componentsProps={{ popper: { sx: { zIndex: 1400 } } }}
        />
    );
};

export const AddressFields = ({ label, value = {}, onChange, country }) => {
    const set = (k) => (v) => onChange({ ...value, [k]: v });
    return (
        <Box>
            {label && <FieldLabel>{label}</FieldLabel>}
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 1.25 }}>
                <Box><TextInput value={value.line1} onChange={set('line1')} placeholder="Address line 1"/></Box>
                <Box><TextInput value={value.line2} onChange={set('line2')} placeholder="Address line 2 — optional"/></Box>
                <TextInput value={value.city} onChange={set('city')} placeholder="City"/>
                {country !== undefined
                    ? <StateSelect country={country} value={value.state} onChange={set('state')}/>
                    : <TextInput value={value.state} onChange={set('state')} placeholder="State, province or region"/>}
                <TextInput value={value.postal_code} onChange={set('postal_code')} placeholder="Postal code"/>
            </Box>
        </Box>
    );
};

export const CheckLine = ({ checked, onChange, children }) => (
    <FormControlLabel
        control={<Checkbox size="small" checked={!!checked} onChange={(e) => onChange(e.target.checked)} sx={{ '&.Mui-checked': { color: ORANGE } }}/>}
        label={<Typography sx={{ fontSize: '0.85rem', lineHeight: 1.5 }}>{children}</Typography>}
        sx={{ alignItems: 'flex-start', '& .MuiCheckbox-root': { pt: 0.4 } }}
    />
);

export const Explainer = ({ title, children }) => (
    <Box sx={{ p: 1.5, borderRadius: '10px', background: 'var(--brand-bg, #F9FAFB)', border: `1px solid ${BORDER}` }}>
        <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', mb: 0.5 }}>{title}</Typography>
        <Typography sx={{ fontSize: '0.8rem', color: MUTED, lineHeight: 1.55 }}>{children}</Typography>
    </Box>
);

/** The document's "[ Go back ]   [ Continue ]", with an optional extra
 * action (e.g. "Show me a different solution") kept on the left. The button
 * group carries its own `ml: auto`, so it hugs the right edge whether it
 * shares the row with `extra` or wraps onto a row of its own on narrow
 * widths — plain `justify-content: space-between` on the outer row can't do
 * that once wrapping leaves only one item per line. */
export const StepActions = ({ onBack, onNext, nextLabel = 'Continue', nextDisabled, busy, backLabel = 'Go back', extra }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', mt: extra ? 0 : 3 }}>
        {extra}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, flexWrap: 'wrap', justifyContent: 'flex-end', ml: 'auto' }}>
            {onBack && (
                <Button variant="outlined" onClick={onBack} disabled={busy}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px', color: 'var(--brand-text, #374151)', borderColor: BORDER }}>
                    {backLabel}
                </Button>
            )}
            {onNext && (
                <Button variant="contained" onClick={onNext} disabled={nextDisabled || busy}
                    sx={{
                        textTransform: 'none', fontWeight: 700, borderRadius: '8px', backgroundColor: ORANGE, color: '#fff', boxShadow: 'none',
                        '&:hover': { backgroundColor: ORANGE_HOVER, color: '#fff', boxShadow: 'none' },
                    }}>
                    {busy ? 'Please wait…' : nextLabel}
                </Button>
            )}
        </Box>
    </Box>
);

export const ErrorList = ({ errors }) => (errors && errors.length ? (
    <Box sx={{ mt: 2, p: 1.5, borderRadius: '8px', background: '#FEF2F2', border: '1px solid #FECACA' }}>
        {errors.slice(0, 6).map((e) => <Typography key={e} sx={{ fontSize: '0.82rem', color: '#991B1B' }}>• {e}</Typography>)}
    </Box>
) : null);
